import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useEnglishText } from "../../text/useEnglishText.js";
import { Moon, Palette, Sun, UserRound } from "lucide-react";
import { Divider, Menu as MuiMenu, MenuItem } from "@mui/material";

// import { ColorModeContext } from "../../App";
import { AuthContext } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { useAppearance } from "../../theme/AppearanceProvider";
import { BACKGROUND_PRESETS, PRIMARY_PRESETS } from "../../theme/appearanceTokens";
import "../../styles/Navbar.css";

const Navbar = ({ isPublic, sidebarOpen, mode, setTheme }) => {
  const { t } = useEnglishText();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [themeAnchor, setThemeAnchor] = useState(null);
  const { preferences, mode: appearanceMode, updatePreferences } = useAppearance();
  const currentMode = mode ?? appearanceMode;

  const chooseTheme = (nextMode) => {
    if (setTheme) setTheme(nextMode);
    else updatePreferences({ mode: nextMode });
    setThemeAnchor(null);
    if (nextMode === "custom") navigate("/dashboard/settings?tab=appearance");
  };

  const choosePreset = (type, value) => {
    updatePreferences({ mode: "custom", [type === "primary" ? "primaryColor" : "backgroundColor"]: value, shellColorSource: type });
    setThemeAnchor(null);
  };

  const renderPresets = (type, presets, label) => (
    <li className="theme-choice-menu__preset-section" role="none">
      <span className="theme-choice-menu__caption">{label}</span>
      <div className="theme-choice-menu__swatches" role="group" aria-label={label}>
        {presets.map((preset) => {
          const selected = currentMode === "custom" && preferences[type === "primary" ? "primaryColor" : "backgroundColor"] === preset.value;
          const name = t(preset.labelKey, preset.name);
          return (
            <button
              key={preset.value}
              type="button"
              className={`theme-choice-menu__swatch ${selected ? "is-selected" : ""}`}
              style={{ "--swatch-color": preset.value }}
              aria-label={`${label}: ${name}`}
              aria-pressed={selected}
              title={name}
              onClick={() => choosePreset(type, preset.value)}
            >
              <span aria-hidden="true" />
            </button>
          );
        })}
      </div>
    </li>
  );

  return (
    <nav
      className={`navbar-container ${!isPublic ? "dashboard-nav" : ""} ${sidebarOpen ? "shifted" : "collapsed"}`}
      dir="ltr"
    >
      <div className="navbar-content">
        <div className="nav-right-section">
          <Link to="/" className="nav-logo">
            <span className="logo-text">{t("AppName")}</span>
          </Link>
        </div>

        <div className="nav-actions">
          <div className="flex">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="icon-action-btn"
              onClick={(event) => setThemeAnchor(event.currentTarget)}
              aria-label={t("common.theme", "Choose appearance")}
              aria-haspopup="menu"
              aria-expanded={Boolean(themeAnchor)}
              title={t("common.theme", "Choose appearance")}
            >
              {currentMode === "dark" ? <Moon size={25} strokeWidth={2} aria-hidden="true" /> : currentMode === "custom" ? <Palette size={25} strokeWidth={2} aria-hidden="true" /> : <Sun size={25} strokeWidth={2} aria-hidden="true" />}
            </Button>
            <MuiMenu className="theme-choice-menu" anchorEl={themeAnchor} open={Boolean(themeAnchor)} onClose={() => setThemeAnchor(null)}>
              <MenuItem selected={currentMode === "light"} onClick={() => chooseTheme("light")}><Sun size={17} aria-hidden="true" /> {t("settings.appearance.light", "Light")}</MenuItem>
              <MenuItem selected={currentMode === "dark"} onClick={() => chooseTheme("dark")}><Moon size={17} aria-hidden="true" /> {t("settings.appearance.dark", "Dark")}</MenuItem>
              <MenuItem selected={currentMode === "custom"} onClick={() => chooseTheme("custom")}><Palette size={17} aria-hidden="true" /> {t("settings.appearance.custom", "Custom")}</MenuItem>
              <Divider className="theme-choice-menu__divider" />
              {renderPresets("primary", PRIMARY_PRESETS, t("settings.appearance.primaryColor", "Primary color"))}
              {renderPresets("background", BACKGROUND_PRESETS, t("settings.appearance.backgroundColor", "Page background"))}
            </MuiMenu>
          </div>

          {/*  منطق شرطی برای نمایش دکمه‌ها */}
          {user ? (
            <Link to="/dashboard/profile" className="signup-btn">
              <UserRound size={23} strokeWidth={2} aria-hidden="true" />
              {user.FULL_NAME?.trim() || user.fullName?.trim() || t("common.incomplete", "Incomplete profile")}
            </Link>
          ) : (
            <>
              <Link to="/auth" className="login-btn">
                {t("auth.login")}
              </Link>
              <Link to="/" className="signup-btn">
                {t("auth.start")}
              </Link>
            </>
          )}

        </div>
      </div>

    </nav>
  );
};

export default Navbar;
